/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, {useState, useEffect, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageShell from '../PageShell';
import { logout } from "../../features/user";
import axios from "axios";
import { API_URL, API_OK, API_EXCEPTION, DEFAULT_ERROR_TEXT, CUSTOMER_VIEW_CUSTOMERS_LIST } from '../../Constants';
import { CUSTOMER_VIEW_CUSTOMER, CUSTOMER_VIEW_BLOCK, CUSTOMER_VIEW_WATCH } from '../../Constants';
import { Backdrop, CircularProgress, Dialog } from '@mui/material';
import { showErrorDialog } from "../../features/errorDialog";
import WatchSystem from '../Customer/WatchSystem';
import cookie from "js-cookie";
import '../../styles.css';
import CustomersGridWrapperMobile from '../Customer/Mobile/GridWrapper';
import Customer from '../Customer/Customer';
import CustomerBlockCategory from '../Customer/BlockCategory';
import {handleApiErrorMessage, getCommunityGeneralInfo, formatLocalPhone} from "../../Helper";
import InnerDialog from "../InnerDialog";

/** value of the "search by" selector that means "search everything" (the default). */
const SMART_SEARCH_FIELD = 'all';
/** how many rows each of the smart search's per-field queries pulls before they are merged. */
const SMART_SEARCH_LIMIT = 5000;
/** how long typing pauses before the list is re-queried. */
const SEARCH_DEBOUNCE_MS = 300;
/** page sizes offered in the customers list. */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

/**
 * Numbers are stored internationally, so a query typed as "05…" also has to match
 * the stored "9725…" form (and the other way round).
 */
const phoneVariants = (value) => {
  const digits = String(value ?? '').replace(/[^0-9]/g, '');
  if(!digits){
    return [];
  }
  const variants = [digits];
  if(digits.startsWith('0')){
    variants.push('972' + digits.slice(1));
  }else if(digits.startsWith('972')){
    variants.push('0' + digits.slice(3));
  }
  return variants;
};

/**
 * The sort selector is written for customers; in the sub-lines view the same choice
 * maps onto the matching field of a line.
 */
const SUB_LINE_SORT_FIELD = {
  name: 'use_name',
  phone: 'number',
  update_date: 'create_date',
  last_out_call: 'last_call',
};

/**
 * Does one aggregated line match the search box? The selected field is honoured;
 * the smart option matches anything on the line or on the customer it belongs to.
 *
 * @param {object} line  an aggregated line (its customer is on _customerName/_customerPhone)
 * @param {string} field the value of the "search by" selector
 * @param {string} value what was typed
 */
const subLineMatchesSearch = (line, field, value) => {
  const term = String(value ?? '').trim().toLowerCase();
  if(term === ''){
    return true;
  }
  const numbers = phoneVariants(term);
  const hasText = (text) => String(text ?? '').toLowerCase().includes(term);
  const hasNumber = (number) => numbers.some((variant) => String(number ?? '').includes(variant));

  switch(field){
    case 'name':     return hasText(line._customerName);
    case 'number':   return hasNumber(line.number) || hasNumber(line._customerPhone);
    case 'lineId':   return String(line.id ?? '').includes(term);
    case 'lineName': return hasText(line.use_name);
    default:
      return hasText(line.use_name) || hasText(line._customerName)
        || hasNumber(line.number) || hasNumber(line._customerPhone)
        || String(line.id ?? '').includes(term);
  }
};

/** A comparator for the aggregated lines, for the field the sort selector points at. */
const subLineComparator = (sortColumn, sortColumnOrder) => (a, b) => {
  const direction = (sortColumnOrder === 'desc') ? -1 : 1;
  const field = SUB_LINE_SORT_FIELD[sortColumn] || 'use_name';
  const aValue = a[field];
  const bValue = b[field];
  if(field === 'create_date' || field === 'last_call'){
    const aTime = aValue ? new Date(aValue).getTime() : 0;
    const bTime = bValue ? new Date(bValue).getTime() : 0;
    if(!isNaN(aTime) && !isNaN(bTime)){
      return (aTime - bTime) * direction;
    }
  }
  return String(aValue ?? '').localeCompare(String(bValue ?? ''), 'he', { numeric: true }) * direction;
};

/**
 * Map a customer's `membership` value to a relationship tag (label + css class),
 * indicating how this community is related to the customer.
 *   PRIMARY       → this community is the customer's primary community.
 *   CUSTOMER_LINK → additional assignment at the customer level.
 *   LINE_LINK     → the community is attached only via the customer's line(s).
 * Returns null for unknown / missing values so no badge renders.
 */
const getCustomerMembershipTag = (membership) => {
  switch (membership) {
    case 'PRIMARY':
      return { label: 'קהילה ראשית', className: 'membership-tag--primary' };
    case 'CUSTOMER_LINK':
      return { label: 'שיוך נוסף (לקוח)', className: 'membership-tag--customer' };
    case 'LINE_LINK':
      return { label: 'שיוך דרך מנוי', className: 'membership-tag--line' };
    default:
      return null;
  }
};

export default function CustomersManagement(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customerView, setCustomerView] = useState(CUSTOMER_VIEW_CUSTOMERS_LIST);
  const initalSelectedCustomerValue = {
    id: null,
    name: null,
    phone: null,
    update_date: null
  };
  const [selectedCustomer, setSelectedCustomer] = useState(initalSelectedCustomerValue);
  const initialLine = {
    id: null,
    number: "",
    use_name: ""
  };
  const [selectedLine, setSelectedLine] = useState(initialLine);
  const [selectedCustomerToDelete, setSelectedCustomerToDelete] = useState(initalSelectedCustomerValue);
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
  const [deletionNote, setDeletionNote] = useState('');
  const [dialogLoaderVisible, setDialogLoaderVisible] = useState(false);
  const [missingDeletionNote, setMissingDeletionNote] = useState(false);
  const [popupPosition, setPopupPosition] = useState(0);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  // community-wide active/inactive tally (from the server, not the current page)
  const [activityStats, setActivityStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(false);
  const DEAFULT_PAGE_SIZE = 25;
  const [pageSize, setPageSize] = useState(DEAFULT_PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState("update_date");
  const [sortColumnOrder, setSortColumnOrder] = useState("asc");
  const [showInactive, setShowInactive] = useState(false);
  // live community setting (the login localStorage value can be stale)
  const [showRedInactiveLines, setShowRedInactiveLines] = useState(localStorage.getItem('show_red_inactive_lines') === "true");
  const initalSearchParameterValue = {
    field: SMART_SEARCH_FIELD,
    value: ''
  };
  const [searchParameter, setSearchParameter] = useState(initalSearchParameterValue);

  // every list request gets a number; a slower answer that isn't the newest one is dropped
  const listRequestId = useRef(0);
  const previousSearchValue = useRef('');

  /* ── smart ("all") search ──
     the API filters by a single field at a time, so a smart search fans the term out to every
     field in parallel and merges the answers; the merged list is sorted and paged on the client. */
  const [smartRows, setSmartRows] = useState(null);
  const smartQueryKey = useRef(null);

  /* ── lines search inside an open customer (lives in the detail header) ── */
  const [lineSearch, setLineSearch] = useState('');

  // a smart search only kicks in once something was typed; an empty box is a plain listing
  const isSmartSearchField = (searchParameter.field === SMART_SEARCH_FIELD);
  const isSmartSearch = isSmartSearchField && (searchParameter.value.trim() !== '');

  /* ── inline desktop list state (nick editing) ── */
  const [editingNickId, setEditingNickId] = useState(null);
  const [editingNickValue, setEditingNickValue] = useState('');
  const [showSavedMessage, setShowSavedMessage] = useState(false);

  /* ── "sub-subscribers": every line across all customers, shown as cards ── */
  const [showSubLines, setShowSubLines] = useState(false);
  const [subLines, setSubLines] = useState([]);
  const [subLinesLoading, setSubLinesLoading] = useState(false);

  useEffect(() => {
    // typing waits a moment so a search isn't fired on every keystroke
    const searchValueChanged = (previousSearchValue.current !== searchParameter.value);
    previousSearchValue.current = searchParameter.value;
    const handle = setTimeout(() => refreshCustomersData(), searchValueChanged ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(handle);
  }, [page, pageSize, searchParameter, sortColumn, sortColumnOrder, showInactive, showRedInactiveLines]);

  // Read the live community config from /getStat so a changed "show red inactive
  // lines" setting is reflected immediately (instead of the stale login value).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const info = await getCommunityGeneralInfo(cookie.get('token'), t, dispatch, showErrorDialog, logout, navigate);
      if (!cancelled && info && info.hasOwnProperty('showRedInactiveLines')) {
        const live = info.showRedInactiveLines === true || info.showRedInactiveLines === "true";
        setShowRedInactiveLines(live);
        localStorage.setItem('show_red_inactive_lines', live);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const changeSearchParameter = (e, type) => {
    let currentSearchParameter = {
      field: searchParameter.field,
      value: searchParameter.value
    };
    
    currentSearchParameter[type] = e.target.value;
    setSearchParameter(currentSearchParameter);
    setPage(0);
    setPageSize(DEAFULT_PAGE_SIZE);
  };

  const handleDeleteCustomer = (cellValues) => {
    setSelectedCustomerToDelete({
      id: cellValues.id,
      name: cellValues.name,
      phone: cellValues.phone,
      update_date: cellValues.update_date
    });
    setDeleteDialogIsOpen(true);
  };

  const resetSelectedCustomerToDelete = () => {
    setSelectedCustomerToDelete(initalSelectedCustomerValue);
  }

  /**
   * The per-field queries a smart search fans out to.
   * Numbers are stored internationally (972…), so "05…" is also tried as "9725…".
   */
  const getSmartSearchQueries = (rawValue) => {
    const value = rawValue.trim();
    const queries = [
      { searchBy: 'name', search: value },
      { searchBy: 'lineName', search: value },
    ];
    const numbers = phoneVariants(value);
    if(numbers.length){
      numbers.forEach((number) => queries.push({ searchBy: 'number', search: number }));
      queries.push({ searchBy: 'lineId', search: numbers[0] });
    }
    return queries;
  };

  /** Run every smart-search query in parallel and return the merged, de-duplicated customers. */
  const fetchSmartCustomers = async (value, activity) => {
    const headers = { token: cookie.get('token') };
    const queries = getSmartSearchQueries(value);

    const responses = await Promise.all(queries.map((query) =>
      axios.post(API_URL + '/getCustomers', {
        start: 0,
        limit: SMART_SEARCH_LIMIT,
        searchBy: query.searchBy,
        search: query.search,
        sort_column: sortColumn,
        sort_order: sortColumnOrder,
        activity,
      }, { headers })
        .then(({ data }) => (data && data.responseStatus === API_OK && Array.isArray(data.customers)) ? data.customers : null)
        .catch(() => null)
    ));

    // every single query failed → treat it as a failed search
    if(responses.every((customers) => customers === null)){
      return null;
    }

    const merged = [];
    const seenIds = new Set();
    responses.forEach((customers) => (customers || []).forEach((customer) => {
      if(!seenIds.has(customer.id)){
        seenIds.add(customer.id);
        merged.push(customer);
      }
    }));
    return merged;
  };

  const runSmartSearch = async (value, activity) => {
    const queryKey = JSON.stringify([value.trim(), activity]);
    // paging & sorting of a loaded smart result happen on the client — don't ask the server again
    if(smartQueryKey.current === queryKey && smartRows !== null){
      return;
    }
    smartQueryKey.current = queryKey;
    const requestId = ++listRequestId.current;
    setLoading(true);

    const merged = await fetchSmartCustomers(value, activity);
    if(requestId !== listRequestId.current){
      return;   // a newer search already took over
    }
    if(merged === null){
      smartQueryKey.current = null;
      handleApiErrorMessage(t(DEFAULT_ERROR_TEXT), t, dispatch, showErrorDialog, logout, navigate);
      setLoading(false);
      return;
    }

    setSmartRows(merged);
    setRows([]);
    setRowCount(merged.length);
    // the server tallies the whole community; for a search result we count what we got
    const inactive = merged.filter((customer) => isInactive(customer)).length;
    setActivityStats({ total: merged.length, active: merged.length - inactive, inactive });
    setLoading(false);
  };

  const refreshCustomersData = (pageToStart = null) => {
    if(pageToStart === null){
      pageToStart = page;
    }
    let errorMessage = t(DEFAULT_ERROR_TEXT);

    // "לא פעילים" מסוננים בשרת רק כשהקהילה מסמנת מנויים לא-פעילים (showRedInactiveLines) והמתג כבוי.
    const activity = (showRedInactiveLines && !showInactive) ? 'active' : 'all';

    if(isSmartSearch){
      runSmartSearch(searchParameter.value, activity);
      return;
    }
    if(smartRows !== null){
      setSmartRows(null);
      smartQueryKey.current = null;
    }

    const requestId = ++listRequestId.current;
    setLoading(true);

    let  headers = {
      token: cookie.get('token')
    }

    axios.post(API_URL + '/getCustomers', {
      start: pageToStart*pageSize,
      limit: pageSize,
      searchBy: (searchParameter.field === SMART_SEARCH_FIELD) ? 'name' : searchParameter.field,
      search: searchParameter.value,
      sort_column: sortColumn,
      sort_order: sortColumnOrder,
      activity,
    }, {headers})
    .then(
      ({ data }) => {
        if(requestId !== listRequestId.current){
          return;   // a newer request already took over
        }
        if(data.hasOwnProperty("responseStatus") && (data["responseStatus"] === API_OK && data.hasOwnProperty("customers"))){
          setRows(data.customers);
          setRowCount(data.totalCount);
          if(data.stats){ setActivityStats(data.stats); }
          setLoading(false);
        }else{
          if((data.responseStatus === API_EXCEPTION) && (data.hasOwnProperty("message"))){
            errorMessage = data["message"];
          }
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          setLoading(false);
        }
      }).catch((error) => {
        if(requestId !== listRequestId.current){
          return;
        }
        handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        setLoading(false);
    });
  };

  const handleDeleteRowSubmit = async () => {
    if(deletionNote === ""){
      setMissingDeletionNote(true);
      return;
    }
    setMissingDeletionNote(false);

    let errorMessage = t(DEFAULT_ERROR_TEXT);

    setDialogLoaderVisible(true);

    let  headers = {
      token: cookie.get('token')
    }
    
      const data = {
        customer_id: selectedCustomerToDelete.id,
        note: deletionNote
      };

      let apiUrl = API_URL + '/outCustomer';

      axios.post(apiUrl, data, {headers})
      .then(
         (responseData) => {
          if(responseData.data.hasOwnProperty('responseStatus') && responseData.data.responseStatus === API_OK){
            refreshCustomersData();
            resetSelectedCustomerToDelete();
            setDialogLoaderVisible(false);
            setDeleteDialogIsOpen(false);
          }else{
            if(responseData.data.responseStatus === API_EXCEPTION && responseData.data.hasOwnProperty("message")){
              errorMessage = responseData.data["message"];
            }
            handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
            setDialogLoaderVisible(false);
          }
        }).catch((error) => {
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          setDialogLoaderVisible(false);
      });
  };

  const exportCustomersLines = async () => {
    let errorMessage = t(DEFAULT_ERROR_TEXT);
    setLoading(true);
  
    const headers = {
      token: cookie.get('token')
    };
  
    axios.post(
      API_URL + `/exportCustomersLines`, 
      {}, 
      { 
        headers, 
        responseType: 'blob'
      }
    )
    .then((response) => {
      const contentType = response.headers['content-type'];
  
      if (contentType && contentType.includes('application/json')) {
        const reader = new FileReader();
        reader.onload = () => {
          const errorData = JSON.parse(reader.result);
          handleApiErrorMessage(errorData.message || errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        };
        reader.readAsText(response.data);
        setLoading(false);
        return;
      }
  
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      link.setAttribute('download', `customers_lines_${new Date().getTime()}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
  
      setLoading(false);
    })
    .catch((error) => {
      console.error("Export Error:", error);
      handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
      setLoading(false);
    });
  };

  const getCustomerPageContent = () => {
    return (customerView === CUSTOMER_VIEW_CUSTOMER) ?
    <Customer 
      isDesktop={props.isDesktop}
      customer={selectedCustomer} 
      lineSearch={lineSearch}
      setCustomerView={setCustomerView}
      selectedLine={selectedLine}
      setSelectedLine={setSelectedLine}
      resetSelectedLine={() => {setSelectedLine(initialLine)}}
       /> : 
       (customerView === CUSTOMER_VIEW_WATCH ? 
          (
            selectedLine.id !== null ?
            <WatchSystem 
              isDesktop={props.isDesktop} 
              entityType="line" 
              line={selectedLine} 
              customer={selectedCustomer}
              setCustomerView={setCustomerView}
                /> :
            <WatchSystem 
              isDesktop={props.isDesktop}  
              entityType="customer" 
              customer={selectedCustomer}
              setCustomerView={setCustomerView}
                />
          )
          :
          <CustomerBlockCategory 
            isDesktop={props.isDesktop}
            customer={selectedCustomer}
            selectedLine={selectedLine}
            setSelectedLine={setSelectedLine}
            resetSelectedLine={() => {setSelectedLine(initialLine)}}
            setCustomerView={setCustomerView}
            />
       )
    ;
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogIsOpen(false);
    setMissingDeletionNote(false);
  }

  /* ── open a customer (full-page detail) ── */
  const openCustomer = (c) => {
    setSelectedCustomer({ id: c.id, name: c.name, phone: c.phone, update_date: c.update_date, last_out_call: c.last_out_call });
    setLineSearch('');
    setCustomerView(CUSTOMER_VIEW_CUSTOMER);
  };

  /* ── sub-subscribers: fetch every customer's lines and present them as cards ── */
  const isLineInactive = (l) => showRedInactiveLines && (!l.last_call || (((new Date() - new Date(l.last_call)) / (1000 * 60 * 60 * 24)) > 5));

  const loadSubLines = async () => {
    setSubLinesLoading(true);
    const headers = { token: cookie.get('token') };

    // The customer list is server-paginated (small pages), so first pull ALL customers,
    // then aggregate every customer's lines. The search box is deliberately NOT applied
    // here: this view is searched, sorted and paged on the client, so narrowing the
    // aggregate now would hide lines whose own name or number matches the query.
    let custs = [];
    try {
      const { data } = await axios.post(API_URL + '/getCustomers', {
        start: 0, limit: 20000,
        searchBy: 'name', search: '',
        sort_column: sortColumn, sort_order: sortColumnOrder,
        activity: 'all',
      }, { headers });
      if (data && data.responseStatus === API_OK && Array.isArray(data.customers)) custs = data.customers;
    } catch (e) { custs = rows; /* fall back to the current page */ }

    const all = [];
    let idx = 0;
    // fetch a few customers' lines in parallel to keep it fast without hammering the API
    const worker = async () => {
      while (idx < custs.length) {
        const c = custs[idx++];
        try {
          const { data } = await axios.post(API_URL + '/getLinesInCustomers',
            { customer_id: c.id, start: 0, limit: 20000, sort_column: 'create_date', sort_order: 'asc' },
            { headers });
          if (data && data.responseStatus === API_OK && Array.isArray(data.lines)) {
            data.lines.forEach((l) => all.push({ ...l, _customerId: c.id, _customerName: c.name, _customerPhone: c.phone }));
          }
        } catch (e) { /* skip a customer whose lines failed to load */ }
      }
    };
    await Promise.all(Array.from({ length: Math.min(8, custs.length) }, () => worker()));
    setSubLines(all);
    setSubLinesLoading(false);
  };

  const toggleSubLines = () => {
    if (showSubLines) { setShowSubLines(false); setPage(0); return; }
    setShowSubLines(true);
    setPage(0);
    loadSubLines();
  };

  // open a single line into the given management view (block list / watch system)
  const openSubLineView = (l, view) => {
    setSelectedCustomer({ id: l._customerId, name: l._customerName, phone: l._customerPhone });
    setLineSearch('');
    setSelectedLine({ id: l.id, number: l.number, use_name: l.use_name });
    setCustomerView(view);
  };

  /* ── edit nick in community ── */
  const startEditNick = (c) => { setEditingNickId(c.id); setEditingNickValue(c.nick_in_community || ''); };
  const cancelEditNick = () => { setEditingNickId(null); setEditingNickValue(''); };
  const saveNick = (customerId, nickInCommunity) => {
    let errorMessage = t(DEFAULT_ERROR_TEXT);
    const headers = { token: cookie.get('token') };
    axios.post(API_URL + '/setCustomerNickInCommunity', { customerId, nickInCommunity }, { headers })
      .then(async (responseData) => {
        if (responseData.data.hasOwnProperty('responseStatus') && responseData.data.responseStatus === API_OK) {
          setLoading(true);
          cancelEditNick();
          await refreshCustomersData();
          setShowSavedMessage(true);
          setTimeout(() => setShowSavedMessage(false), 2000);
        } else {
          if (responseData.data.responseStatus === API_EXCEPTION && responseData.data.hasOwnProperty("message")) errorMessage = responseData.data["message"];
          setLoading(false);
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        }
      }).catch(() => { handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate); setLoading(false); });
  };

  /**
   * Order two customers by the selected sort column — the smart search merges several
   * server queries, so its ordering has to be redone here.
   */
  const compareCustomers = (a, b) => {
    const direction = (sortColumnOrder === 'desc') ? -1 : 1;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if(sortColumn === 'update_date' || sortColumn === 'last_out_call'){
      const aTime = aValue ? new Date(aValue).getTime() : 0;
      const bTime = bValue ? new Date(bValue).getTime() : 0;
      if(!isNaN(aTime) && !isNaN(bTime)){
        return (aTime - bTime) * direction;
      }
    }
    return String(aValue ?? '').localeCompare(String(bValue ?? ''), 'he', { numeric: true }) * direction;
  };

  // rows on screen: a server page normally, a client-side slice of the merged smart result otherwise
  const displayedCustomers = (smartRows !== null)
    ? [...smartRows].sort(compareCustomers).slice(page*pageSize, (page+1)*pageSize)
    : rows;

  /* ══ inline DESKTOP customers list (cards) ══ */
  // Only treat lines as "inactive" when the community is set to mark inactive lines
  // (live value from /getStat). Otherwise every line counts as active.
  const isInactive = (c) => showRedInactiveLines && (!c.last_out_call || (((new Date() - new Date(c.last_out_call)) / (1000 * 60 * 60 * 24)) > 5));
  const renderDesktopList = () => {
    const busy = showSubLines ? subLinesLoading : loading;

    // The sub-lines overlay is aggregated in the browser, so its search, activity filter,
    // ordering and paging all run here; customer rows are filtered and paged by the server.
    const searchedSubLines = showSubLines
      ? subLines.filter((l) => subLineMatchesSearch(l, searchParameter.field, searchParameter.value))
      : [];
    const matchedSubLines = showSubLines
      ? searchedSubLines.filter((l) => showInactive || !isLineInactive(l)).sort(subLineComparator(sortColumn, sortColumnOrder))
      : [];

    // counts: server-wide tallies for customers; over the searched set for the overlay.
    // (when the community doesn't mark inactive lines, everyone counts as active — matching isInactive())
    const inactiveCount = showSubLines
      ? searchedSubLines.filter(isLineInactive).length
      : (showRedInactiveLines ? (activityStats.inactive || 0) : 0);
    const activeCount = showSubLines
      ? (searchedSubLines.length - inactiveCount)
      : (showRedInactiveLines ? (activityStats.active || 0) : (activityStats.total || 0));

    const totalRows = showSubLines ? matchedSubLines.length : rowCount;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    // a shrinking result set can leave `page` past the end
    const currentPage = Math.min(page, totalPages - 1);
    const visibleRows = showSubLines
      ? matchedSubLines.slice(currentPage*pageSize, (currentPage+1)*pageSize)
      : displayedCustomers;
    const goToPage = (p) => setPage(Math.min(Math.max(0, p), totalPages - 1));
    return (
      <div className="customers-wrapper cust-cards-mode">
        {showSavedMessage && <div className='saved-message'>{t('Changes saved')}</div>}
        <div className='title'>
          <div className='text'>{t('Customers connected to the community')}</div>
          <div className='cust-stats'>
            <label className='cust-inactive-toggle'>
              <span className='cust-inactive-toggle-label'>הצג גם לא פעילים</span>
              <button
                type='button'
                role='switch'
                aria-checked={showInactive}
                className={'cust-switch' + (showInactive ? ' on' : '')}
                onClick={() => { setPage(0); setShowInactive(v => !v); }}
              >
                <span className='cust-switch-knob' />
              </button>
            </label>
            <span className='cust-stat cust-stat--active'><span className='cust-stat-dot' />{activeCount} פעילים</span>
            <span className='cust-stat cust-stat--inactive'><span className='cust-stat-dot' />{inactiveCount} לא פעילים</span>
          </div>
          <button type='button' className={'cust-sublines-btn' + (showSubLines ? ' on' : '')} onClick={toggleSubLines}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            <span>{showSubLines ? 'הצג מנויים' : 'הצג תתי מנויים'}</span>
          </button>
          <div className='export' onClick={() => exportCustomersLines()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span>{t('Export')}</span>
          </div>
        </div>

        <div className='cust-toolbar'>
          <div className='cust-sort'>
            <div className='cust-sort-group'>
              <span className='sort-by-label'>{t('Sort by:')}</span>
              <select className='sort-column psh-select' value={sortColumn} onChange={(e) => { setPage(0); setSortColumn(e.target.value); }}>
                <option value="name">{showSubLines ? t('Line Use Name') : t('Customer Name')}</option>
                <option value="phone">{t('Number')}</option>
                <option value="update_date">{showSubLines ? t('Created at') : t('Last Updated at')}</option>
                <option value="last_out_call">{t('Last Call')}</option>
              </select>
            </div>
            <div className='cust-sort-group'>
              <span className='sort-by-label'>{t('Sort order:')}</span>
              <select className='sort-order psh-select' value={sortColumnOrder} onChange={(e) => setSortColumnOrder(e.target.value)}>
                <option value="asc">{t('Ascending')}</option>
                <option value="desc">{t('Descending')}</option>
              </select>
            </div>
            <div className='cust-sort-group'>
              <span className='sort-by-label'>הצג לעמוד:</span>
              <select className='psh-select' value={pageSize} onChange={(e) => { setPage(0); setPageSize(Number(e.target.value)); }}>
                {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </div>
          </div>
          <div className='cust-search'>
            <span className='cust-search-label'>חיפוש לפי:</span>
            <select className='psh-select' value={searchParameter.field} onChange={(e) => changeSearchParameter(e, 'field')}>
              <option value={SMART_SEARCH_FIELD}>{t("All (smart search)")}</option>
              <option value="name">{t("Customer name")}</option>
              <option value="number">{t("Number")}</option>
              <option value="lineId">{t("Line ID")}</option>
              <option value="lineName">{t("Line Use Name")}</option>
            </select>
            <div className='cust-search-input'>
              <svg className='cust-search-icon' width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" className="search"
                placeholder={isSmartSearchField ? t("Name, number, line name or line ID") : t("Search")}
                value={searchParameter.value} onChange={(e) => changeSearchParameter(e, 'value')} />
            </div>
          </div>
        </div>

        <div className='cust-cards-area'>
          {busy && <div className='cust-loading'><span className='cust-loading-spinner' /><span className='cust-loading-text'>{showSubLines ? 'טוען תתי מנויים' : 'טוען נתונים'}</span></div>}
          {visibleRows.length === 0 && !busy ? (
            <div className='cust-empty'>{showSubLines ? 'לא נמצאו תתי מנויים' : t("No rows found")}</div>
          ) : showSubLines ? (
            <div className='cust-cards'>
              {visibleRows.map((l) => {
                const inactive = isLineInactive(l);
                return (
                  <div className='cust-card' key={l._customerId + '-' + l.id} onClick={() => openSubLineView(l, CUSTOMER_VIEW_BLOCK)}>
                    <div className='cust-card-head'>
                      <div className='cust-card-avatar'>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                      </div>
                      <div className='cust-card-id'>
                        <div className='cust-card-name'>
                          <span className='cust-card-name-txt'>{l.use_name || formatLocalPhone(l.number)}</span>
                        </div>
                        <div className='cust-card-phone'>{formatLocalPhone(l.number) || '—'}</div>
                      </div>
                      <span className={'cust-card-status' + (inactive ? ' off' : '')}>{inactive ? 'לא פעיל' : 'פעיל'}</span>
                    </div>
                    <div className='cust-card-info'>
                      <div className='cust-card-row'>
                        <span className='cust-card-row-label'>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          מנוי
                        </span>
                        <span className='cust-card-row-val cust-card-row-val--name'>{l._customerName || '—'}</span>
                      </div>
                      <div className='cust-card-row'>
                        <span className='cust-card-row-label'>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          תאריך הצטרפות
                        </span>
                        <span className='cust-card-row-val'>{l.create_date || '—'}</span>
                      </div>
                      <div className='cust-card-row'>
                        <span className='cust-card-row-label'>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                          שיחה אחרונה
                        </span>
                        <span className={'cust-card-row-val' + (inactive ? ' inactive' : '')}>{l.last_call || '—'}</span>
                      </div>
                    </div>
                    <div className='cust-card-line-actions'>
                      <button className='cust-line-act cust-line-act--watch' onClick={(e) => { e.stopPropagation(); openSubLineView(l, CUSTOMER_VIEW_WATCH); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>
                        <span>מערכת שעון</span>
                      </button>
                      <button className='cust-line-act cust-line-act--block' onClick={(e) => { e.stopPropagation(); openSubLineView(l, CUSTOMER_VIEW_BLOCK); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        <span>רשימת חסימה</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='cust-cards'>
              {visibleRows.map((c) => {
                const inactive = isInactive(c);
                return (
                  <div className='cust-card' key={c.id} onClick={() => openCustomer(c)}>
                    <button className='cust-card-del' onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(c); }} title={t("Delete 2")}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <div className='cust-card-head'>
                      <div className='cust-card-avatar'>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <div className='cust-card-id'>
                        <div className='cust-card-name'>
                          <span className='cust-card-name-txt'>{c.name}</span>
                          <button className='cust-nick-pencil' title={t('Nick in Community')} onClick={(e) => { e.stopPropagation(); startEditNick(c); }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                        {editingNickId === c.id ? (
                          <div className='cust-nick-edit' onClick={(e) => e.stopPropagation()}>
                            <input type='text' autoFocus value={editingNickValue} placeholder={t('Nick in Community')}
                              onChange={(e) => setEditingNickValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveNick(c.id, editingNickValue); if (e.key === 'Escape') cancelEditNick(); }} />
                            <button className='cust-nick-save' onClick={() => saveNick(c.id, editingNickValue)} title={t('Save')}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button className='cust-nick-cancel' onClick={cancelEditNick} title={t('Cancel')}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ) : (
                          <div className='cust-card-phone'>{c.nick_in_community || formatLocalPhone(c.phone) || '—'}</div>
                        )}
                        {(() => {
                          const tag = getCustomerMembershipTag(c.membership);
                          return tag ? (
                            <div className='cust-card-tags'>
                              <span className={'membership-tag ' + tag.className}>{tag.label}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                      <span className={'cust-card-status' + (inactive ? ' off' : '')}>{inactive ? 'לא פעיל' : 'פעיל'}</span>
                    </div>
                    <div className='cust-card-info'>
                      <div className='cust-card-row'>
                        <span className='cust-card-row-label'>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          תאריך הצטרפות
                        </span>
                        <span className='cust-card-row-val'>{c.join_date || c.create_date || c.update_date || '—'}</span>
                      </div>
                      <div className='cust-card-row'>
                        <span className='cust-card-row-label'>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                          שיחה אחרונה
                        </span>
                        <span className={'cust-card-row-val' + (inactive ? ' inactive' : '')}>{c.last_out_call || '—'}</span>
                      </div>
                    </div>
                    <button className='cust-card-manage' onClick={(e) => { e.stopPropagation(); openCustomer(c); }}>
                      <span>ניהול</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {totalRows > 0 && (
          <div className='cust-pager'>
            <button
              type='button'
              className='cust-pager-btn'
              disabled={currentPage <= 0 || busy}
              onClick={() => goToPage(currentPage - 1)}
              aria-label={t('Previous')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className='cust-pager-info'>
              עמוד {currentPage + 1} מתוך {totalPages} · {totalRows} רשומות
            </span>
            <button
              type='button'
              className='cust-pager-btn'
              disabled={currentPage >= totalPages - 1 || busy}
              onClick={() => goToPage(currentPage + 1)}
              aria-label={t('Next')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  const isCustomerSubView = customerView === CUSTOMER_VIEW_WATCH || customerView === CUSTOMER_VIEW_BLOCK;
  const subViewEntityType = selectedLine.id !== null ? 'קו' : 'מנוי';
  const subViewEntityName = selectedLine.id !== null
    ? (selectedLine.use_name ? `${selectedLine.use_name} | ${formatLocalPhone(selectedLine.number)}` : formatLocalPhone(selectedLine.number))
    : (selectedCustomer.name || '');
  const subViewAction = customerView === CUSTOMER_VIEW_WATCH
    ? 'הגדרת מערכת שעון'
    : (customerView === CUSTOMER_VIEW_BLOCK ? 'ניהול רשימת חסימה' : '');
  const backBtnText = isCustomerSubView ? 'חזרה לניהול הלקוח' : 'חזרה לרשימת הלקוחות';
  const backBtnTarget = isCustomerSubView ? CUSTOMER_VIEW_CUSTOMER : CUSTOMER_VIEW_CUSTOMERS_LIST;

  return (
    <PageShell title="ניהול מנויים" subtitle="ניהול רשימת מנויי הקהילה">
    <>
      {(customerView === CUSTOMER_VIEW_CUSTOMERS_LIST) ?
        (props.isDesktop ? renderDesktopList() :
          <CustomersGridWrapperMobile
            isDesktop={props.isDesktop}
            setCustomerView={setCustomerView}
            setSelectedCustomer={setSelectedCustomer}
            handleDeleteCustomer={handleDeleteCustomer}
            exportCustomersLines={exportCustomersLines}
            page={page}
            rows={displayedCustomers}
            pageSize={pageSize}
            rowCount={rowCount}
            loading={loading}
            setPage={setPage}
            setPageSize={setPageSize}
            changeSearchParameter={changeSearchParameter}
            searchParameter={searchParameter}
          />
        )
        :
        <div className='customer-detail-page'>
          <div className='cust-detail-header'>
            <button className='cust-back-btn' onClick={() => { if(!isCustomerSubView){ setLineSearch(''); } setCustomerView(backBtnTarget); }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              <span>{backBtnText}</span>
            </button>
            {customerView === CUSTOMER_VIEW_CUSTOMER && (
              <div className='cust-detail-search'>
                <span className='cust-detail-search-label'>{t('Search line')}:</span>
                <div className='cust-search-input'>
                  <svg className='cust-search-icon' width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input type="text" className="search"
                    placeholder={t('Line name, number or line ID')}
                    value={lineSearch}
                    onChange={(e) => setLineSearch(e.target.value)} />
                  {lineSearch !== '' && (
                    <button type='button' className='cust-search-clear' onClick={() => setLineSearch('')} title={t('Cancel')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                </div>
              </div>
            )}
            {subViewAction && (
              <div className='cust-detail-subtitle'>
                <span className='cust-detail-subtitle-ic'>
                  {customerView === CUSTOMER_VIEW_WATCH ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                  )}
                </span>
                <span className='cust-detail-subtitle-action'>{subViewAction}</span>
                <span className='cust-detail-subtitle-sep'>עבור</span>
                <span className='cust-detail-subtitle-entity'>
                  <span className='cust-detail-subtitle-entity-type'>{subViewEntityType}</span>
                  <span className='cust-detail-subtitle-entity-name'>{subViewEntityName}</span>
                </span>
              </div>
            )}
          </div>
          {getCustomerPageContent()}
        </div>
      }
      {!props.isDesktop &&
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      }
      <Dialog open={deleteDialogIsOpen} onClose={handleCloseDeleteDialog} PaperProps={{ className: 'bl-dialog-paper' }}>
        <div className='bl-dialog'>
          <div className='bl-dialog-header bl-dialog-header--danger'>
            <div className='bl-dialog-icon'>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="22" y2="13"/><line x1="22" y1="8" x2="17" y2="13"/></svg>
            </div>
            <h3 className='bl-dialog-title'>הסרת לקוח מהקהילה</h3>
            <button className='bl-dialog-close' onClick={handleCloseDeleteDialog}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className='bl-dialog-body'>
            <p className='cat-join-text'>הלקוח <strong>{selectedCustomerToDelete.name}</strong> עומד להיות מוסר מהקהילה לצמיתות.</p>
            <div className='cat-join-warning'>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>שימו לב!! פעולה זו הינה <strong>בלתי הפיכה</strong>.</span>
            </div>
            <div className='bl-field'>
              <label htmlFor="deletion-note">{t('Reason for removal')}</label>
              <input id="deletion-note" type="text" className='bl-input'
                placeholder={t('Reason for removal')}
                value={deletionNote}
                onChange={(e) => setDeletionNote(e.target.value)}
              />
              {missingDeletionNote && <div className='bl-field-error'>* {t("Required field")}</div>}
            </div>
          </div>
          <div className='bl-dialog-footer'>
            <button className='bl-dialog-cancel' onClick={handleCloseDeleteDialog}>{t('Cancel')}</button>
            <button className='bl-dialog-submit bl-dialog-submit--danger' onClick={handleDeleteRowSubmit}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              {t("Remove")}
            </button>
          </div>
          <Backdrop
            sx={{ color: '#fff', position: 'absolute', borderRadius: '18px', zIndex: 5 }}
            open={dialogLoaderVisible}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </div>
      </Dialog>
      </>
    </PageShell>
    );
}
